import React, { Fragment, useMemo } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useAppSelector } from '../../../../redux/hooks';
import { stepsSelector } from '../../../../redux/selectors/builder';
import { IRowMeta, RowType } from '../../../../redux/slices/oldBuilder';
import { StepEventMenuOpenContextProvider } from '../../../../utils/contexts/StepEventMenuOpenContext';
import * as BuilderUI from '../../../../components/Builder/BuilderUI';
import BuilderStepOrder from '../../../../components/Builder/BuilderStep/BuilderStepOrder';
import { AliasHighlight } from '../../../../components/Builder/AliasNavigation/AliasHighlight';
import BuilderStep from '../../../../components/Builder/BuilderStep';
import { MAX_STEP_COUNT } from '../../../../config/constants';
import { BuilderAddRowAction } from '../../../../components/Builder/BuilderActions';
import DndDroppableZone from '../../../../components/DndEntity/DndDroppableZone';
import DropzoneArea from '../../../../components/DndEntity/DndDroppableZone/DropzoneArea';
import { EntityDropzoneType } from '../../../../components/QuickAccessDictionary/DictionaryTabs/types';
import { useDndEntityInternalContext } from '../../../../components/DndEntity';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../../components/DndEntity/utils';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
} from '../../../../components/DndEntity/validation';
import { isEmptyEventConditions } from '../../../../utils/builder';

const FunnelBuilderSteps = () => {
  const steps = useAppSelector(stepsSelector);
  const { activeItem } = useDndEntityInternalContext();

  const eventDropzoneRowMeta = {
    rowType: RowType.steps,
    rowIndex: steps.length,
    isLastStep: true,
    eventIndex: 0,
  };
  const eventDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Events,
    eventDropzoneRowMeta,
  );

  const canDropEvent = useMemo(() => {
    if (activeItem) {
      const allEventConditionsFilled = steps.every((step) => {
        return !isEmptyEventConditions(step.eventConditions);
      });

      const data = getDndActiveItemData(activeItem);
      if (data && allEventConditionsFilled) {
        return (
          data.__typename === 'TagLeafAnyEvent' ||
          data.__typename === 'TagLeafBasicEvent' ||
          data.__typename === 'TagLeafVirtualEvent'
        );
      }
    }
    return false;
  }, [activeItem, steps]);

  return (
    <>
      <Droppable droppableId={RowType.steps} direction="vertical">
        {(provided) => (
          <BuilderUI.SectionColumn
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {steps.map((step, stepIndex) => {
              const rowMeta: IRowMeta = {
                rowIndex: stepIndex,
                rowType: RowType.steps,
                isLastStep: stepIndex === steps.length - 1,
              };
              const hasStepOrder = stepIndex > 0;

              return (
                <Fragment key={`funnel-builder-form-step-${stepIndex}`}>
                  {hasStepOrder && <BuilderStepOrder rowMeta={rowMeta} />}
                  <Draggable
                    isDragDisabled={steps.length === 1}
                    draggableId={RowType.steps + stepIndex}
                    index={stepIndex}
                  >
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}>
                        <AliasHighlight aliasId={step.userAliasId}>
                          <StepEventMenuOpenContextProvider>
                            <BuilderStep
                              rowMeta={rowMeta}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </StepEventMenuOpenContextProvider>
                        </AliasHighlight>
                      </div>
                    )}
                  </Draggable>
                </Fragment>
              );
            })}
            {provided.placeholder}
          </BuilderUI.SectionColumn>
        )}
      </Droppable>
      {steps.length < MAX_STEP_COUNT && !canDropEvent && (
        <BuilderAddRowAction
          rowMeta={{ rowType: RowType.steps, rowIndex: steps.length - 1 }}
          label={'+ Add Step'}
          trackContext="builder"
          trackAction="add step"
        />
      )}
      {steps.length < MAX_STEP_COUNT && canDropEvent && (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Events]}
          id={eventDropzoneId}
          data={
            {
              id: eventDropzoneId,
              type: EntityDropzoneDataTypeEnum.Events,
              rowMeta: eventDropzoneRowMeta,
              isNewRow: true,
            } satisfies IEventEntityDropzoneDataType
          }
        >
          <DropzoneArea size="large">Add Event as Step</DropzoneArea>
        </DndDroppableZone>
      )}
    </>
  );
};

export default FunnelBuilderSteps;
